import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { TFPageContent } from '@tf/shared';
import { Button, Center, Flex, Icon, TFCard, TFLoadingOverlay, TFText } from '@tf/ui';
import { T } from '@tf/utils';
import { Navigate, useParams } from 'react-router-dom';
import { InformationRequest, LivenessCheckRequest } from '@/components/cases';
import { CompanyLogo, ErrorCard } from '@/components/shared';
import type { IdentityVerificationRequest, InformationRequest as InformationRequestType } from '@/core/api';
import { useCaseRequest } from '@/core/api';
import { companyLandingPaths } from '@/routes/companyLanding/paths';

export const CaseRequestRoute = () => {
	const { requestId } = useParams();
	T.assertNotNullish(requestId, 'no requestId in url');

	const { _ } = useLingui();

	const caseRequestQuery = useCaseRequest(requestId);

	if (caseRequestQuery.isPending) {
		return <TFLoadingOverlay />;
	}

	return (
		<TFPageContent maw="50rem" m="0" flex="1">
			<Center mb="16px">
				<CompanyLogo />
			</Center>

			{caseRequestQuery.isError ? (
				<ErrorCard
					icon={<Icon.IconExclamationCircle />}
					title={_(msg({ id: 'caseRequestPage.loadingError.title', message: 'Failed to load case request' }))}
					description={_(msg({ id: 'caseRequestPage.loadingError.message', message: 'Please try again' }))}
					primaryAction={
						<Button mt="1rem" type="button" onClick={() => caseRequestQuery.refetch()}>
							{_(
								msg({
									id: 'caseRequestPage.loadingError.action',
									message: 'Reload page',
								}),
							)}
						</Button>
					}
				/>
			) : caseRequestQuery.data.status === 'UPDATED' ? (
				<Navigate to={companyLandingPaths.requestSubmitted()} />
			) : caseRequestQuery.data.status === 'REVOKED' ? (
				<TFCard innerPadding="2rem">
					<Flex justify="center">
						<Icon.IconCircleCheck color="#51C564" size="40px" />
					</Flex>

					<Trans id="caseRequestPage.canceledMessage">
						<TFText ta="center" size="26px" fw="600">
							This request has been canceled
						</TFText>
						<TFText mt="0.5rem" ta="center">
							We will be in touch should we require any further information.
						</TFText>
					</Trans>
				</TFCard>
			) : 'caseKind' in caseRequestQuery.data && caseRequestQuery.data.caseKind === 'identity_verification' ? (
				<LivenessCheckRequest caseRequest={caseRequestQuery.data as IdentityVerificationRequest} />
			) : (
				<InformationRequest caseRequest={caseRequestQuery.data as InformationRequestType} />
			)}
		</TFPageContent>
	);
};
